import type { Mission } from "@rogoag/shared-ts";
import type { Job } from "@domain/CRM/job";
import type { CoreUpdateInput, FudgeCoreInput } from "@domain/MissionControl";
import type { IMissionLoader } from "@ports/missions/IMissionLoader";
import type { IMissions } from "@ports/missions/IMissions";
import type { IJobIssues } from "@ports/jobs/IJobIssues";

export interface MissionsUseCasesDeps {
	missionLoader: IMissionLoader;
	missionsApi: IMissions;
	jobIssuesApi: Pick<IJobIssues, "fixExeMissionValidationJobs">;
}

/**
 * Bound mission use cases consumed by the GUI/CLI. The loader half delegates
 * to the injected `IMissionLoader` (whose adapter owns the per-Job caches);
 * the mutate half wraps the `IMissions` calls with a follow-up call to
 * `IJobIssues.fixExeMissionValidationJobs` so the job's "issues" status
 * reflects the new core coordinates. Each method is independently testable
 * by importing the underlying function and passing a stub deps object — the
 * factory exists to bundle the deps once at composition time so consumers
 * see a single namespace.
 */
export interface MissionsUseCases {
	loadExe(job: Job): Promise<Mission | undefined>;
	loadInt(job: Job): Promise<Mission | undefined>;
	loadPtsShp(job: Job, expectedType?: string): Promise<Mission | "no-attachment" | "download-failed" | "parse-failed">;
	fudgeCores(jobId: string, inputs: FudgeCoreInput[]): Promise<void>;
	updateCores(jobId: string, inputs: CoreUpdateInput[]): Promise<void>;
	deleteCores(jobId: string, coreIds: string[]): Promise<void>;
	setAverageCoreLength(jobId: string, coreIds: string[]): Promise<void>;
}

/**
 * Submits a fudge-core payload to the Missions backend and triggers the
 * exe-mission-validation pipeline. No-ops cleanly when `inputs` is empty so
 * callers don't need their own empty-list guard. Caller is responsible for
 * the visible countdown + cache invalidation + reselect that follow.
 */
export async function fudgeCores(deps: MissionsUseCasesDeps, jobId: string, inputs: FudgeCoreInput[]): Promise<void> {
	if (inputs.length === 0) return;
	await deps.missionsApi.fudgeCores(jobId, inputs);
	await deps.jobIssuesApi.fixExeMissionValidationJobs([jobId]);
}

/**
 * Submits a manual core-edit payload (pulled lat/lon + length) to the Missions
 * backend and triggers the exe-mission-validation pipeline. No-ops cleanly when
 * `inputs` is empty. Caller owns the visible countdown + cache invalidation + reselect.
 */
export async function updateCores(deps: MissionsUseCasesDeps, jobId: string, inputs: CoreUpdateInput[]): Promise<void> {
	if (inputs.length === 0) return;
	await deps.missionsApi.updateCores(jobId, inputs);
	await deps.jobIssuesApi.fixExeMissionValidationJobs([jobId]);
}

/** Deletes a list of cores by id, then triggers exe-mission-validation. */
export async function deleteCores(deps: MissionsUseCasesDeps, jobId: string, coreIds: string[]): Promise<void> {
	await deps.missionsApi.deleteCores(jobId, coreIds);
	await deps.jobIssuesApi.fixExeMissionValidationJobs([jobId]);
}

/** Sets the supplied cores' length to the average of all cores for that
 *  job, then triggers exe-mission-validation. */
export async function setAverageCoreLength(
	deps: MissionsUseCasesDeps,
	jobId: string,
	coreIds: string[],
): Promise<void> {
	await deps.missionsApi.setAverageCoreLength(jobId, coreIds);
	await deps.jobIssuesApi.fixExeMissionValidationJobs([jobId]);
}

export function makeMissionsUseCases(deps: MissionsUseCasesDeps): MissionsUseCases {
	return {
		loadExe: (job) => deps.missionLoader.loadExe(job),
		loadInt: (job) => deps.missionLoader.loadInt(job),
		loadPtsShp: (job, expectedType) => deps.missionLoader.loadPtsShp(job, expectedType),
		fudgeCores: (jobId, inputs) => fudgeCores(deps, jobId, inputs),
		updateCores: (jobId, inputs) => updateCores(deps, jobId, inputs),
		deleteCores: (jobId, coreIds) => deleteCores(deps, jobId, coreIds),
		setAverageCoreLength: (jobId, coreIds) => setAverageCoreLength(deps, jobId, coreIds),
	};
}
